import React, { useState, useEffect, useRef } from 'react';
import {
  PageSection,
  useGlobal,
  globalizationPath,
  Header,
  NavLink,
  Link,
  RowCard,
} from '@rcg/ui-react';
import { navigate } from '@gatsbyjs/reach-router/';
import InfoIcon from '@material-ui/icons/Info';
import { Box, Typography } from '@material-ui/core';
import { useMutation } from '@apollo/client';
import { formatPrice } from '@rcg/pricing-format';
import { PayWithCreditCard, Itinerary } from '../../components';
import PaymentStyles from './index.style';
import { getSessionStorageItem, showAffirm, importAffirmScript } from '../../commons/utils';
import { openAffirmModal } from '../../commons/payment';
import {
  SESSION_STORAGE_CURRENT_SAILING,
  SESSION_STORAGE_PRICE_BOOKING,
  SESSION_STORAGE_CURRENT_BOOKING,
  ROOT_PATH,
  NAVIGATION_PATHS,
  AFFIRM_LOGO,
} from '../../commons/constants';
import PaymentOption from '../../components/PaymentOption';
import Loader from '../../components/Loader';
import {
  ENABLE_GIFT_CARDS_PAYMENT,
  ENABLE_3DS_PAYMENT_GATEWAY,
  ENABLE_GIFT_CARD_TOKENIZATION,
  ENABLE_CVV_COLLECTION,
} from '../../commons/ld-user';
import GiftCards from '../../components/GiftCards';
import { MAKE_PAYMENT } from '../../graphql/mutations';
import {
  setupPaywayInterval,
  handlePaymentApiAndThreeDs,
  runGatewayPaywayValidation,
  displayErrorNotification,
  displayErrorNotificationAffirm,
  displayWarningNotification,
} from '../../commons/3dsValidation';
import { handlePaymentError } from '../../commons/paymentNotifications';
import { trackAffirmInteraction, trackPageView } from '../../commons/tracking/tracking';
import PaymentFooterWithModal from '../../components/PaymentFooterWithModal';
import NotificationBannerComponent from '../../components/NotificationBanner';
import useCommonStyles from '../../commons/useCommonStyles';
import Dictionary from '../../commons/dictionary';
import useDictionary from '../../hooks/useDictionary';

const validateErrors = (
  errorAmountToPay,
  disableCreditCardForm,
  errorCreditCard,
  affirmChecked,
) => {
  if (affirmChecked) {
    return false;
  }
  return errorAmountToPay || (!disableCreditCardForm && errorCreditCard);
};

const Payment = ({ ldFlags }) => {
  const classes = PaymentStyles();
  const styles = useCommonStyles();
  const getDictionaryValue = useDictionary(Dictionary);
  const {
    renderContent,
    data: { lang, market, country },
  } = useGlobal();
  const urlPath = globalizationPath(market, lang, ROOT_PATH);
  const [amountToPay, setAmountToPay] = useState(0);
  const [radioButtonSelected, setRadioButtonSelected] = useState('');
  const [showLoader, setShowLoader] = useState(true);
  const [giftLoader, setGiftLoader] = useState(false);
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [errorAmountToPay, setErroramountToPay] = useState(true);
  const [disableGiftCards, setDisableGiftCards] = useState(true);
  const [errorCreditCard, setErrorCreditCard] = useState(true);
  const [makePaymentInfo, setMakePaymentInfo] = useState({});
  const [handleSubmit, setHandleSubmit] = useState(null);
  const [paymentResponseInfo, setPaymentresponseInfo] = useState(null);
  const [errorPayment, setErrorPayment] = useState(null);
  const [propNotification, setNotification] = useState({
    title: '',
    description: '',
    type: '',
    open: false,
  });

  const [giftCards, setGiftCard] = useState([]);
  const [disableCreditCardForm, setDisableCreditCardForm] = useState(false);
  const [creditCardData, setCreditCardData] = useState({});
  const [currentBalanceToPay, setCurrentBalanceToPay] = useState(0);
  const [affirmChecked, setAffirmChecked] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [ldFlagsParams, setldFlagsParams] = useState({
    country: '',
    language: '',
  });

  const [isCheckedCruiseTicket, setIsCheckedCruiseTicket] = useState(true);
  const [showLineError, setShowLineError] = useState(false);
  const [firstValueCheckbox, setFirstValueCheckbox] = useState(true);

  const currentBookingInfo = getSessionStorageItem(SESSION_STORAGE_CURRENT_BOOKING);
  const ldFlagsCountry = currentBookingInfo.country;

  useEffect(() => {
    if (currentBookingInfo) {
      setldFlagsParams({ ...ldFlagsParams, ldFlagsCountry, language: lang });
    }
  }, []);

  const royalPayway = useRef(null);
  const { REACT_APP_AIR_3DS_ENV, REACT_APP_API_KEY_AFFIRM, REACT_APP_AFFIRM_SCRIPT } = process.env;
  const payment3dsToggle = ldFlags?.[ENABLE_3DS_PAYMENT_GATEWAY];
  const ldAirFlagsGiftCard = ldFlags?.[ENABLE_GIFT_CARDS_PAYMENT];
  const ldFlags3DsTokenizeGiftCard = ldFlags?.[ENABLE_GIFT_CARD_TOKENIZATION];
  const enableCvvForCreditCardPayment = ldFlags?.[ENABLE_CVV_COLLECTION];
  const sailingData = getSessionStorageItem(SESSION_STORAGE_CURRENT_SAILING);
  const priceData = getSessionStorageItem(SESSION_STORAGE_PRICE_BOOKING);
  const { affirm } = window;
  const currentBooking = { price: priceData, id: priceData.bookingId };
  const threeDsComponentElement = royalPayway.current;
  const payRequired = priceData.balanceDue > 0;
  const showAffirmCheckBox = radioButtonSelected === 'remainingBalance';

  const [makePayment] = useMutation(MAKE_PAYMENT, {
    onCompleted: (data) => {
      setPaymentresponseInfo(data);
      setShowLoader(false);
      setIsPaymentLoading(false);
      navigate(`${urlPath}/${NAVIGATION_PATHS[0].dashboard}`, {
        state: {
          paymentResponse: data,
          priceData,
          payments: makePaymentInfo,
          paymentOption: affirmChecked ? 'affirm' : 'normal',
        },
      });
    },
    onError: (error) => {
      window.scrollTo(0, 0);
      setErrorPayment({
        amount: makePaymentInfo?.variables?.makePaymentInput?.[0]?.amount,
        remainingBalance: priceData.balanceDue,
      });
      const globalData = { lang, market };
      handlePaymentError(error, renderContent, setNotification, globalData);
      setShowLoader(false);
      setIsPaymentLoading(false);
    },
  });

  const isAffirm = showAffirm(priceData.currencyCode, priceData.balanceDue, ldFlags);

  useEffect(() => {
    if (ldFlags) {
      trackPageView({ currency: priceData.currencyCode, isAffirm });
    }
  }, [ldFlags]);

  useEffect(() => {
    importAffirmScript(REACT_APP_API_KEY_AFFIRM, REACT_APP_AFFIRM_SCRIPT);
    return () => {
      if (window.affirm) {
        window.affirm.ui.modal.close();
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (makePaymentInfo && makePaymentInfo.variables) {
      makePayment(makePaymentInfo);
    }
    // eslint-disable-next-line
  }, [makePaymentInfo]);

  useEffect(() => {
    if ((payment3dsToggle && royalPayway) || (ldFlags3DsTokenizeGiftCard && royalPayway)) {
      setupPaywayInterval(royalPayway);

      // eslint-disable-next-line
    }
  }, [payment3dsToggle, ldFlags3DsTokenizeGiftCard]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [propNotification]);

  useEffect(() => {
    if (radioButtonSelected !== 'remainingBalance') {
      setDisableCreditCardForm(false);
      setDisableGiftCards(false);
      setAffirmChecked(false);
    }

    if (affirm) {
      affirm.ui.ready(() => {
        affirm.ui.refresh();
      });
      if (affirm.events) {
        affirm.events.on('prequal:close', () => {
          trackAffirmInteraction('closed');
        });
      }
    }
  }, [radioButtonSelected, affirm]);

  useEffect(() => {
    if (affirmChecked) {
      setDisableCreditCardForm(true);
      setDisableGiftCards(true);
    } else {
      setDisableCreditCardForm(false);
      setDisableGiftCards(false);
    }
  }, [affirmChecked]);

  const showErrorNotification = () => {
    window.scrollTo(0, 0);
    displayErrorNotification(setNotification, renderContent);
  };

  const showErrorNotificationAffirm = () => {
    window.scrollTo(0, 0);
    displayErrorNotificationAffirm(setNotification, renderContent);
  };

  const disableMakePayment = validateErrors(
    errorAmountToPay,
    disableCreditCardForm,
    errorCreditCard,
    affirmChecked,
  );

  const submitPayment = () => {
    setIsPaymentLoading(true);
    if (disableMakePayment || firstValueCheckbox || !isCheckedCruiseTicket) {
      if (!isCheckedCruiseTicket || firstValueCheckbox) {
        setFirstValueCheckbox(false);
        setShowLineError(true);
        setIsCheckedCruiseTicket(false);
      }
    } else {
      if (affirmChecked && currentBookingInfo) {
        setShowLoader(true);
        openAffirmModal(
          currentBookingInfo,
          setShowLoader,
          threeDsComponentElement,
          country,
          setMakePaymentInfo,
          showErrorNotificationAffirm,
          setAffirmChecked,
          priceData.balanceDue,
          ldFlags,
        );
        trackAffirmInteraction('checkoutStart');
      } else if (disableCreditCardForm) {
        handlePaymentApiAndThreeDs(
          threeDsComponentElement,
          null, // no values provided from creditCardForm in this flow
          null, // no threeDsSession needed in this flow
          setShowLoader,
          setMakePaymentInfo,
          giftCards,
          currentBooking,
          country,
          showErrorNotification,
          ldFlags,
        );
      } else {
        handleSubmit.current.handleSubmit();
      }
      setNotification({
        title: '',
        description: '',
        type: '',
        open: false,
      });
    }
  };

  const showWarningNotification = () => {
    window.scrollTo(0, 0);
    displayWarningNotification(setNotification, renderContent);
  };

  const launchPaywayGateway = (variables) => {
    const values = { ...variables };
    const { amount } = values.variables;
    const month = values.variables.expiryMonth;
    const year = values.variables.expiryYear;
    const { cardNumber } = values.variables;
    const { cvv } = values.variables || null;
    if (payment3dsToggle && royalPayway) {
      runGatewayPaywayValidation({
        cardNumber,
        amount,
        variables,
        cvv,
        royalPayway,
        setShowLoader,
        country,
        month,
        year,
        currentBooking,
        giftCards,
        handlePaymentApiAndThreeDs,
        showWarningNotification,
        showErrorNotification,
        setMakePaymentInfo,
      });
    } else {
      setShowLoader(true);
      handlePaymentApiAndThreeDs(
        threeDsComponentElement,
        variables,
        null, // no threeDsSession needed
        setShowLoader,
        setMakePaymentInfo,
        giftCards,
        currentBooking,
        country,
        showErrorNotification,
        ldFlags,
      );
    }
  };

  const handleAffirmCheck = () => {
    setAffirmChecked(!affirmChecked);
  };

  useEffect(() => {
    if (creditCardData.variables) {
      launchPaywayGateway(creditCardData);
    }
    // eslint-disable-next-line
  }, [creditCardData]);

  const giftCardBox = [];
  if (giftCards.length > 0 && !affirmChecked) {
    giftCards.forEach((giftCard) => {
      const boxGiftcard = (
        <Box className={classes.tooltipRow} key={giftCard.info.cardNumber}>
          <span>
            {renderContent('bookingAmendPaymentFooterAmountGiftCard', 'Amount on gift card ')}
            {giftCard.info.cardNumber.substr(-4)}
          </span>
          <span>{giftCard.charged}</span>
        </Box>
      );
      giftCardBox.push(boxGiftcard);
    });
  }

  const remainingBalance = priceData.balanceDue - amountToPay;

  const tooltipContent = (
    <Box className={classes.tooltipWrapper}>
      <Box className={classes.tootipTilte}>
        {renderContent('bookingAmendPaymentFooterPaymentDetail', 'Payment detail')}
        {affirmChecked && <img alt="affirm-logo" src={AFFIRM_LOGO} />}
      </Box>
      <Box>
        {giftCardBox}
        {!disableCreditCardForm && showPaymentMethod && !affirmChecked && (
          <>
            <Box className={classes.tooltipRow}>
              <span>
                {renderContent(
                  'bookingAmendPaymentFooterAmountCreditCard',
                  'Amount on credit card',
                )}{' '}
                {creditCardData.cardNumber !== undefined && creditCardData.cardNumber.length > 4
                  ? creditCardData.cardNumber.substr(-4)
                  : ''}
              </span>
              <span>
                {formatPrice(currentBalanceToPay, priceData.currencyCode, true, true, true)}
              </span>
            </Box>
          </>
        )}
        <Box className={classes.tooltipTotal}>
          <span>
            {affirmChecked
              ? renderContent('bookingAmendPaymentFooterTotalPayWithAffirm', 'Paying with Affirm')
              : renderContent('bookingAmendPaymentFooterTotalPayToday', 'Total payments today')}
          </span>
          <span>{formatPrice(amountToPay, priceData.currencyCode, true, true, true)}</span>
        </Box>
        {!affirmChecked ? (
          <Box className={classes.tooltipRemainingBalance}>
            <span>
              {renderContent('bookingAmendPaymentFooterRemainingBalance', 'Remaining balance')}
            </span>
            <span>{formatPrice(remainingBalance, priceData.currencyCode, true, true, true)}</span>
          </Box>
        ) : (
          ''
        )}
      </Box>
    </Box>
  );

  const tooltipLabel = (
    <Box className={classes.tooltipLabel}>
      <Link component="button" underline="none">
        {formatPrice(amountToPay, priceData.currencyCode, true, true, true)}
      </Link>
      <InfoIcon className={classes.infoIcon} color="primary" />
    </Box>
  );

  return (
    <>
      <Loader
        display={showLoader || giftLoader}
        message={
          !isPaymentLoading &&
          !giftLoader &&
          getDictionaryValue(Dictionary.loaderPaymentRedirect.key)
        }
      />
      <Header position="relative" elevation={0} className={classes.header}>
        <NavLink component="a" href={`${urlPath}/dashboard`}>
          <Typography variant="body2" className={classes.backLabel}>
            {renderContent('amendHeaderBackToCruise', 'Back to your cruise reservation')}
          </Typography>
        </NavLink>
      </Header>

      <Box className={classes.rootPayment}>
        <Box className={classes.contentWrapper}>
          {propNotification.open && (
            <Box className={classes.defaultNotification}>
              <div className={styles.notification}>
                <NotificationBannerComponent
                  {...propNotification}
                  renderCloseBtn
                  isBannerOpen
                  onClose={() => setNotification({ open: false })}
                />
              </div>
            </Box>
          )}
          <aside>
            {sailingData && (
              <Itinerary
                itinerary={sailingData}
                paymentOrigin
                className={classes.itinerary}
                bookingId={currentBookingInfo.id}
              />
            )}
          </aside>
          <main className={classes.main}>
            {propNotification.open && (
              <Box className={classes.desktopNotification}>
                <div className={styles.notification}>
                  <NotificationBannerComponent
                    {...propNotification}
                    renderCloseBtn
                    isBannerOpen
                    onClose={() => setNotification({ open: false })}
                  />
                </div>
              </Box>
            )}
            <royal-payway-three-ds
              env={REACT_APP_AIR_3DS_ENV}
              payment-channel="RW"
              ref={royalPayway}
              data-testid="royal-payway"
            />
            <PageSection
              title={
                payRequired
                  ? renderContent('bookingAmendPaymentOptionTitle', 'Select a payment option')
                  : renderContent('bookingAmendPaidInFullTitle', 'Payment history')
              }
              description={
                payRequired &&
                renderContent(
                  'bookingAmendPaymentOptionDescription',
                  'Please choose to pay off your remaining balance, or pay a custom amount instead. Then enter and verify your credit card information before submitting your payment. You can only make payments online if you have booked directly with Royal Caribbean.',
                )
              }
              className={classes.pageSectionWrapper}
            />
            <PaymentOption
              price={priceData}
              rooms={[]}
              setAmountToPay={setAmountToPay}
              setRadioButtonSelected={setRadioButtonSelected}
              setNotification={setNotification}
              setShowLoader={setShowLoader}
              setCurrentBalanceToPay={setCurrentBalanceToPay}
              setShowPaymentMethod={setShowPaymentMethod}
              setErroramountToPay={setErroramountToPay}
              errorAmountToPay={errorAmountToPay}
              setDisableGiftCards={setDisableGiftCards}
            />
            {payRequired && showPaymentMethod && (
              <>
                <PageSection
                  title={renderContent('bookingAmendPaymentMethodTitle', 'Select a payment method')}
                />
                {isAffirm && (
                  <RowCard
                    enableCheckBox={showAffirmCheckBox}
                    onChangeCheckBox={handleAffirmCheck}
                    checkboxChecked={affirmChecked}
                    className={classes.affirmOption}
                    checkboxLabel={renderContent(
                      'bookingAmendAffirmCheckBoxLabel',
                      'Use this payment method',
                    )}>
                    {showAffirmCheckBox ? (
                      <>
                        <span className="affirmTotalBalance">
                          {renderContent(
                            'bookingAmendAffirmPayTotalBalance',
                            'Pay the total balance.',
                          )}
                          &nbsp;
                          <span
                            className="affirm-as-low-as"
                            data-page-type="category"
                            data-learnmore-show="false"
                            aria-hidden="true"
                            label="button"
                            role="button"
                            tabIndex="0"
                            onClick={() => {
                              trackAffirmInteraction('opened');
                            }}
                            data-amount={Number(priceData.balanceDue)
                              .toFixed(2)
                              .toString()
                              .replace('.', '')}
                          />
                        </span>
                      </>
                    ) : (
                      <Box className={classes.affirmCustom}>
                        <span className="labelAffirmCustom">
                          {renderContent(
                            'bookingAmendAffirmWithCustomAmount',
                            'Select the total balance and pay over time with',
                          )}
                        </span>
                        &nbsp;
                        <img alt="affirm-logo" src={AFFIRM_LOGO} />
                      </Box>
                    )}
                  </RowCard>
                )}

                {ldAirFlagsGiftCard > 0 && priceData.currencyCode === 'USD' && (
                  <div className={classes.giftCards}>
                    <GiftCards
                      radioButtonSelected={radioButtonSelected}
                      totalPrice={priceData}
                      amountValue={amountToPay}
                      showErrorGiftCardsBanner={setNotification}
                      setLoader={setGiftLoader}
                      setGiftCard={setGiftCard}
                      currency={priceData.currency}
                      setDisableCreditCardForm={setDisableCreditCardForm}
                      giftCards={giftCards}
                      setCurrentBalanceToPay={setCurrentBalanceToPay}
                      currentBalanceToPay={currentBalanceToPay}
                      maximumNumberOfGiftCards={ldAirFlagsGiftCard}
                      disableGiftCards={affirmChecked || disableGiftCards || disableCreditCardForm}
                      affirmChecked={affirmChecked}
                    />
                  </div>
                )}
                <PayWithCreditCard
                  price={priceData}
                  paymentResult={paymentResponseInfo}
                  paymentErrorInfo={errorPayment}
                  disableCreditCardForm={disableCreditCardForm}
                  radioButtonSelected={radioButtonSelected}
                  amountToPay={amountToPay}
                  currentBalanceToPay={currentBalanceToPay}
                  setCreditCardData={setCreditCardData}
                  creditCardData={creditCardData}
                  setDisableGiftCards={setDisableGiftCards}
                  setErrorCreditCard={setErrorCreditCard}
                  setHandleSubmit={setHandleSubmit}
                  affirmChecked={affirmChecked}
                  showCvvField={enableCvvForCreditCardPayment}
                />
              </>
            )}
          </main>
        </Box>
        {payRequired && (
          <PaymentFooterWithModal
            tooltipContent={tooltipContent}
            tooltipLabel={tooltipLabel}
            submitPayment={submitPayment}
            setFirstValueCheckbox={setFirstValueCheckbox}
            isCheckedCruiseTicket={isCheckedCruiseTicket}
            setIsCheckedCruiseTicket={setIsCheckedCruiseTicket}
            showLineError={showLineError}
            setShowLineError={setShowLineError}
            affirmChecked={affirmChecked}
            isPaymentPage
          />
        )}
      </Box>
    </>
  );
};

export default Payment;
